// v2.1 (D): the screens the auth gate shows instead of the app: Landing (signed out on "/"), sign in, sign up,
// an invite link, the pending account ("enter an invite code / request access") and session errors.
// Clerk's components come from the lazy ./clerk chunk; they only render when Clerk is on (inside its provider).
import { lazy, Suspense, useEffect, useRef, useState, type FormEvent } from "react";
import { api } from "../api";
import { director } from "../director";
import { Landing } from "../landing/Landing";
import { Link, navigate } from "../router";
import { AuthFrame, Field, InviteScreen, LoginScreen, PasswordInviteForm, PasswordLoginForm, TermsCheck } from "../screens/Auth";
import { boot } from "../session";
import { set, useStore } from "../store";
import type { CreditPack } from "../types";
import { TERMS_VERSION } from "../legal/consent";
import { Fold } from "../components/Fold";
import { authApi } from "./authApi";
import { clerkEnabled, passwordEnabled, type Gate } from "./gate";
import { forgetInvite, getAuth, rememberedInvite, rememberInvite, signOutClerk, useAuthState } from "./state";
import "./auth.css";

const ClerkSignIn = lazy(() => import("./clerk").then((m) => ({ default: m.ClerkSignIn })));
const ClerkSignUp = lazy(() => import("./clerk").then((m) => ({ default: m.ClerkSignUp })));

function ClerkSlot({ which }: { which: "in" | "up" }) {
  return (
    <div className="auth-clerk">
      <Suspense fallback={<p className="auth-clerk-wait">Loading sign-in</p>}>{which === "in" ? <ClerkSignIn /> : <ClerkSignUp />}</Suspense>
    </div>
  );
}

/** The password form as a fold-out under Clerk (authMode "both"). v4 (V4-P2): a Fold, remembered per id. */
function PasswordAlt({ id, label, children }: { id: string; label: string; children: React.ReactNode }) {
  return (
    <Fold id={id} title={label} className="auth-alt">
      {children}
    </Fold>
  );
}

/** Signs out of the api session and Clerk, and stops the Director. */
export async function signOutEverywhere() {
  await api.logout().catch(() => undefined);
  director.stop("signout");
  if (getAuth().clerk?.signedIn) await signOutClerk();
  set({ auth: "out", me: null });
  navigate("/", { replace: true });
}

export function AuthScreen({ gate }: { gate: Exclude<Gate, { screen: "app" } | { screen: "splash" }> }) {
  switch (gate.screen) {
    case "landing":
      // no Clerk: there's no self sign-up, so "request an invite" leads to sign-in (which says it's invite-only)
      return <Landing onSignIn={() => navigate("/login")} onRequestInvite={() => navigate(clerkEnabled(getAuth().config) ? "/signup" : "/login")} />;
    case "sign-in":
      if (!gate.clerk) return <LoginScreen />; // no Clerk: exactly the old screen
      return (
        <AuthFrame title="Welcome back" lede="Sign in and pick up where you and your Director left off." foot={<>CAO Studio is invite-only for now. New here? <Link to="/signup">Create your account</Link>, then enter your invite code.</>}>
          <ClerkSlot which="in" />
          {gate.password && (
            <PasswordAlt id="auth.password" label="Sign in with your CAO Studio password">
              <PasswordLoginForm />
            </PasswordAlt>
          )}
        </AuthFrame>
      );
    case "sign-up":
      return (
        <AuthFrame
          title="Join CAO Studio"
          lede="We're invite-only for now. Create your account with Google or your email, then enter your invite code or ask for access."
          foot={
            <>
              Already have an account? <Link to="/login">Sign in</Link>
            </>
          }
        >
          <ClerkSlot which="up" />
        </AuthFrame>
      );
    case "invite":
      return <InviteGate code={gate.code} clerk={gate.clerk} password={gate.password} />;
    case "pending":
      return <PendingScreen suspended={!!gate.suspended} geo={!!gate.geo} />;
    case "terms":
      return <TermsScreen />;
    case "signup-closed":
      return <SignupClosedScreen />;
    case "session-error":
      return <SessionError reason={gate.reason} />;
  }
}

function InviteGate({ code, clerk, password }: { code: string; clerk: boolean; password: boolean }) {
  useEffect(() => {
    if (clerk) rememberInvite(code); // applied on the pending screen right after sign-up
  }, [clerk, code]);
  if (!clerk) return <InviteScreen code={code} />; // no Clerk: exactly the old screen
  return (
    <AuthFrame
      title="You're invited"
      lede="Make videos by talking them through. Create your account with Google or your email; your invite is applied as soon as you're in."
      foot={
        <>
          Already have an account? <Link to="/login">Sign in</Link> and your invite still applies.
        </>
      }
    >
      <ClerkSlot which="up" />
      {password && (
        <PasswordAlt id="auth.password-signup" label="Or create a password account">
          <PasswordInviteForm code={code} />
        </PasswordAlt>
      )}
    </AuthFrame>
  );
}

// ------------------------------------------------------------------ pending: invite code / request access
// Beta gate: the studio opens only when Keaton lets someone in (an approval, an invite, or a restore). Credit packs show
// here only when the api says a purchase would let a waiting account in (PURCHASE_UNLOCKS; off by default). A
// suspended account sees the same frame with request access only: no code, no packs, no password connect.

export function PendingScreen({ suspended = false, geo = false }: { suspended?: boolean; geo?: boolean }) {
  const me = useStore((s) => s.me);
  const [code, setCode] = useState(() => rememberedInvite() ?? "");
  const [busy, setBusy] = useState<"redeem" | "request" | string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [requestedAt, setRequestedAt] = useState<string | null>(me?.user.accessRequestedAt ?? null);
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const tried = useRef(false);

  const redeem = async (c: string) => {
    if (!c.trim()) return;
    setBusy("redeem");
    setError(null);
    try {
      await authApi.redeemInvite(c);
      forgetInvite();
      await boot(); // now active: the gate opens the app
    } catch (err) {
      forgetInvite(); // a bad remembered code shouldn't retry on every visit
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };

  // an invite link carried through sign-up: apply it straight away, once
  useEffect(() => {
    if (tried.current || suspended) return;
    tried.current = true;
    const remembered = rememberedInvite();
    if (remembered) void redeem(remembered);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (suspended || geo) return;
    api
      .packs()
      .then((p) => setPacks(p.enabled && p.pendingCanBuy ? p.packs : []))
      .catch(() => setPacks([]));
  }, [suspended]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    void redeem(code);
  };
  const request = async () => {
    setBusy("request");
    setError(null);
    try {
      setRequestedAt((await authApi.requestAccess(note)).accessRequestedAt ?? new Date().toISOString());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  };
  const buy = async (packId: string) => {
    setBusy(packId);
    setError(null);
    try {
      location.href = (await api.checkout(packId)).url;
    } catch (err) {
      setError((err as Error).message);
      setBusy(null);
    }
  };

  if (suspended)
    return (
      <AuthFrame
        title="Your access is paused"
        lede={`Signed in as ${me?.user.email ?? "you"}. Your projects and credits are safe. If you think this is a mistake, tell us and we'll take a look.`}
        foot={
          <button type="button" className="auth-link" onClick={() => void signOutEverywhere()}>
            Sign out
          </button>
        }
      >
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        {requestedAt ? (
          <p className="auth-note" role="status">
            Thanks, we got your message ({new Date(requestedAt).toLocaleDateString()}). We'll reply at {me?.user.email ?? "your email"}.
          </p>
        ) : (
          <div className="auth-form">
            <label className="field">
              <span className="field-label">Anything we should know? (optional)</span>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={500} />
            </label>
            <button type="button" className="btn btn-primary btn-block" onClick={() => void request()} disabled={!!busy}>
              {busy === "request" ? "Sending" : "Ask to be let back in"}
            </button>
          </div>
        )}
      </AuthFrame>
    );

  // v4 LG2: a new account from a country where sign-ups are closed during the beta. The waitlist instead of "request
  // access"; an invite code (or an /invite link, applied above) still lets them in; an old password account still
  // connects (existing accounts are never locked out).
  if (geo)
    return (
      <AuthFrame
        title="Not in your country yet"
        lede={GEO_LEDE}
        foot={
          <button type="button" className="auth-link" onClick={() => void signOutEverywhere()}>
            Sign out
          </button>
        }
      >
        <WaitlistForm initialEmail={me?.user.email ?? ""} />
        <div className="auth-or" role="separator">
          <span>have an invite?</span>
        </div>
        <form className="auth-form" onSubmit={submit}>
          <Field label="Invite code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} autoComplete="one-time-code" autoCapitalize="characters" spellCheck={false} placeholder="K7Q2M9XAB3" required />
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          <button type="submit" className="btn btn-quiet btn-block" disabled={!!busy}>
            {busy === "redeem" ? "Checking your code" : "Use my invite"}
          </button>
        </form>
        {passwordEnabled(getAuth().config) && <ConnectPassword email={me?.user.email ?? ""} />}
      </AuthFrame>
    );

  return (
    <AuthFrame
      title="You're almost in"
      lede={`Signed in as ${me?.user.email ?? "you"}. CAO Studio is invite-only for now: enter your invite code, or ask for access.`}
      foot={
        <button type="button" className="auth-link" onClick={() => void signOutEverywhere()}>
          Sign out
        </button>
      }
    >
      <form className="auth-form" onSubmit={submit}>
        <Field label="Invite code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} autoComplete="one-time-code" autoCapitalize="characters" spellCheck={false} placeholder="K7Q2M9XAB3" required />
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <button type="submit" className="btn btn-primary btn-block" disabled={!!busy}>
          {busy === "redeem" ? "Checking your code" : "Use my invite"}
        </button>
      </form>

      <div className="auth-or" role="separator">
        <span>no code?</span>
      </div>

      {requestedAt ? (
        <p className="auth-note" role="status">
          Thanks, your request is in ({new Date(requestedAt).toLocaleDateString()}). We'll be in touch at {me?.user.email ?? "your email"} when there's a seat.
        </p>
      ) : (
        <div className="auth-form">
          <label className="field">
            <span className="field-label">What would you make? (optional)</span>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={500} />
          </label>
          <button type="button" className="btn btn-quiet btn-block" onClick={() => void request()} disabled={!!busy}>
            {busy === "request" ? "Sending" : "Request access"}
          </button>
        </div>
      )}

      {passwordEnabled(getAuth().config) && <ConnectPassword email={me?.user.email ?? ""} />}

      {packs.length > 0 && (
        <div className="auth-packs">
          <p className="auth-note">Or skip the line: a credit pack opens your studio.</p>
          {packs.map((p) => (
            <button key={p.id} type="button" className="btn btn-quiet btn-block" onClick={() => void buy(p.id)} disabled={!!busy}>
              {busy === p.id ? "Opening checkout" : `${p.name}: ${p.credits.toLocaleString()} credits, $${p.usd}`}
            </button>
          ))}
        </div>
      )}
    </AuthFrame>
  );
}

// ------------------------------------------------------------------ v4 LG2: closed countries and the Terms

const GEO_LEDE = "CAO Studio isn't available in your country yet during the beta. Leave your email and we'll tell you when it is.";

/** "Tell me when it's open": POST /api/waitlist. */
function WaitlistForm({ initialEmail }: { initialEmail: string }) {
  const [email, setEmail] = useState(initialEmail);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await authApi.joinWaitlist(email);
      setDone(email.trim());
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  if (done)
    return (
      <p className="auth-note auth-done" role="status">
        Thanks. We'll email {done} when CAO Studio opens in your country.
      </p>
    );
  return (
    <form className="auth-form" onSubmit={submit}>
      <Field label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" inputMode="email" required />
      {error && (
        <p className="form-error" role="alert">
          {error}
        </p>
      )}
      <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
        {busy ? "Saving" : "Tell me when it's open"}
      </button>
    </form>
  );
}

/** Signed out on /signup from a country where sign-ups are closed: the waitlist before any account is made. An
 *  /invite link still shows the normal sign-up. */
function SignupClosedScreen() {
  return (
    <AuthFrame
      title="Not in your country yet"
      lede={GEO_LEDE}
      wrap={false} // no account is made here: just an email on the waitlist
      foot={
        <>
          Got an invite link? Open it to sign up. Already have an account? <Link to="/login">Sign in</Link>
        </>
      }
    >
      <WaitlistForm initialEmail="" />
    </AuthFrame>
  );
}

/** The Terms checkbox: right after sign-up, and once more whenever the Terms version changes. */
export function TermsScreen() {
  const me = useStore((s) => s.me);
  const [agreed, setAgreed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const again = !!me?.user.termsVersion; // they accepted an older version
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!agreed) return;
    setBusy(true);
    setError(null);
    try {
      await authApi.acceptTerms(me?.user.currentTermsVersion || TERMS_VERSION);
      await boot(); // termsRequired is false now: the gate moves on
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };
  return (
    <AuthFrame
      title={again ? "We've updated our Terms" : "One last step"}
      lede={
        again
          ? "Please read the new Terms and Acceptable Use Policy, then tick the box to keep using CAO Studio."
          : `Signed in as ${me?.user.email ?? "you"}. Before you start, please read and accept our Terms and Acceptable Use Policy.`
      }
      wrap={false}
      foot={
        <button type="button" className="auth-link" onClick={() => void signOutEverywhere()}>
          Sign out
        </button>
      }
    >
      <form className="auth-form" onSubmit={submit}>
        <TermsCheck checked={agreed} onChange={setAgreed} />
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <button type="submit" className="btn btn-primary btn-block" disabled={busy || !agreed}>
          {busy ? "Saving" : "Continue"}
        </button>
      </form>
    </AuthFrame>
  );
}

/** An account made with a password before Clerk (its email never verified) isn't joined automatically: proving its
 *  password here connects it to this sign-in, with its projects and credits (POST /api/auth/link-password). */
function ConnectPassword({ email: initial }: { email: string }) {
  const [email, setEmail] = useState(initial);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await authApi.linkPassword(email, password);
      await boot(); // the connected account is active: the gate opens the app
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <PasswordAlt id="auth.connect" label="Already made an account with a password? Connect it">
      <form className="auth-form" onSubmit={submit}>
        <Field label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" inputMode="email" required />
        <Field label="Password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required />
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <button type="submit" className="btn btn-quiet btn-block" disabled={busy}>
          {busy ? "Connecting" : "Connect my account"}
        </button>
      </form>
    </PasswordAlt>
  );
}

// ------------------------------------------------------------------ errors

function SessionError({ reason }: { reason: "rejected" | "unavailable" }) {
  const msg = useAuthState((s) => s.sessionError);
  if (reason === "unavailable")
    return (
      <AuthFrame title="Sign-in didn't load" lede="Check your connection, or allow this site in your content blocker, then try again." foot={<>CAO Studio signs you in with Google or your email.</>}>
        <button type="button" className="btn btn-primary btn-block" onClick={() => location.reload()}>
          Try again
        </button>
      </AuthFrame>
    );
  return (
    <AuthFrame title="Couldn't open your studio" lede={msg || "You're signed in, but the studio didn't accept the session."} foot={<>If this keeps happening, sign out and back in.</>}>
      <div className="auth-form">
        <button type="button" className="btn btn-primary btn-block" onClick={() => void boot().catch(() => undefined)}>
          Try again
        </button>
        <button type="button" className="btn btn-quiet btn-block" onClick={() => void signOutEverywhere()}>
          Sign out
        </button>
      </div>
    </AuthFrame>
  );
}
